import { Button } from 'primereact/button';
import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import NumberInput from '../../../../../../../../../../../../components/forms/number-input/NumberInput';
import SelectButtonInput from '../../../../../../../../../../../../components/forms/select-button-input/SelectButtonInput';
import TextInplaceEdit from '../../../../../../../../../../../../components/forms/text-inplace-edit/TextInplaceEdit';
import TooltipIcon from '../../../../../../../../../../../../components/tooltip-icon/TooltipIcon';
import { ActionType, OperationMode } from '../../../../PcfWizard.type';
import styles from './AuxiliaryMachineForm.module.css';
import { getButtonLabel, getPlaceholder } from './helper/getPlaceHolder';

interface AuxiliaryMachineFormProps {
  processIndex: number;
  machineIndex: number;
}

const excludedValuesForAddAnotherMachine = [OperationMode.DEFAULT];

// Get the values of the operation mode enum and filter them
const operationModeOptions = Object.values(OperationMode)
  .filter((value) => {
    return (
      typeof value === 'string' &&
      !excludedValuesForAddAnotherMachine.includes(value as OperationMode)
    );
  })
  .map((value) => {
    return {
      label: getButtonLabel(value as OperationMode),
      value,
    };
  });

const AuxiliaryMachineForm = ({
  processIndex,
  machineIndex,
}: AuxiliaryMachineFormProps): JSX.Element => {
  const { control, getValues, setValue } = useFormContext();
  const operationModeControl = useWatch({
    name: `processes[${processIndex}].machines[${machineIndex}].operation_mode`,
    control,
  });

  const { t } = useTranslation();

  // Get the operation mode of the machine
  const operationMode = useMemo(() => {
    const data = getValues(
      `processes[${processIndex}].machines[${machineIndex}].operation_mode`,
    ) as OperationMode;

    return data;
  }, [operationModeControl, machineIndex, processIndex]) as OperationMode;

  // Determine whether the edit machine name option is enabled/not
  const editMachineName =
    getValues(`processes[${processIndex}].machines[${machineIndex}].action`) === ActionType.ADD;

  // Listen to the machines array to get the latest data
  const machines =
    useWatch({
      name: `processes[${processIndex}].machines`,
    }) || [];

  const onRemoveMachine = (index: number) => {
    const newMachines = [...machines];
    newMachines.splice(index, 1);
    setValue(`processes[${processIndex}].machines`, newMachines);
  };

  return (
    <div className='flex flex-column surface-ground border-round-2xl p-3 mb-3'>
      <div className='flex flex-row align-items-center justify-content-between'>
        <div className='w-full'>
          {editMachineName ? (
            <TextInplaceEdit
              name={`processes[${processIndex}].machines[${machineIndex}].name`}
              placeholder={t(
                'footprintDetailsPage.sections.calculatePcfSection.injectionMoldingSection.inputs.placeholders.machineName',
              )}
              value={getValues(`processes[${processIndex}].machines[${machineIndex}].name`)}
              control={control}
              textSize='text-sm'
              className='font-medium pl-0'
            />
          ) : (
            <h4 className='font-medium' style={{ fontSize: '14px' }}>
              {getValues(`processes[${processIndex}].machines[${machineIndex}].name`)}
            </h4>
          )}
        </div>
        <Button
          icon='pi pi-times'
          className={`p-button-text p-button-rounded p-button-md ${styles.closeButton}`}
          onClick={() => {
            onRemoveMachine(machineIndex);
          }}
        />
      </div>

      <div className='grid'>
        {/* Operation Mode selection */}
        {operationMode !== OperationMode.DEFAULT && (
          <div className='col-12'>
            <label
              htmlFor={`processes[${processIndex}].machines[${machineIndex}].operation_mode`}
              className='font-medium mb-2 block'
              style={{ fontSize: '12px' }}
            >
              {t(
                'footprintDetailsPage.sections.calculatePcfSection.injectionMoldingSection.inputs.placeholders.operationMode',
              )}
            </label>
            <SelectButtonInput
              className='my-2'
              pt={{
                button: { className: 'px-4 text-sm' },
              }}
              name={`processes[${processIndex}].machines[${machineIndex}].operation_mode`}
              control={control}
              options={operationModeOptions}
              defaultValue={
                getValues(`processes[${processIndex}].machines[${machineIndex}].name`) === 'Robot'
                  ? OperationMode.HANDLING_TIME
                  : undefined
              }
            />
          </div>
        )}

        <div className='col-7'>
          {/* Operating Power input */}
          <NumberInput
            name={`processes[${processIndex}].machines[${machineIndex}].rated_power`}
            control={control}
            label={t(
              'footprintDetailsPage.sections.calculatePcfSection.injectionMoldingSection.inputs.placeholders.ratedPower',
            )}
            suffix=' kW'
            minFractionDigits={0}
            maxFractionDigits={2}
            customIcon={
              getValues(`processes[${processIndex}].machines[${machineIndex}].name`) === 'Robot' ? (
                <TooltipIcon
                  id={`process${processIndex}_machine${machineIndex}_ratedPower`}
                  position='right'
                  tooltipText={t(
                    'footprintDetailsPage.sections.calculatePcfSection.injectionMoldingSection.inputs.toolTipTexts.ratedPowerRobotAuxillaryMachine',
                  )}
                />
              ) : undefined
            }
          />
        </div>

        {/* Handling Time input */}
        {operationMode === OperationMode.HANDLING_TIME && (
          <div className='col-5'>
            <NumberInput
              name={`processes[${processIndex}].machines[${machineIndex}].operation_value`}
              control={control}
              label={getPlaceholder(operationMode)}
              suffix={' s'}
              minFractionDigits={0}
              maxFractionDigits={2}
            />
          </div>
        )}

        {/* Info text for Hopper Dryer machine */}
        {getValues(`processes[${processIndex}].machines[${machineIndex}].name`) ===
          'Hopper Dryer' && (
          <span className='flex text-gray-500 px-2 -mt-2' style={{ fontSize: '12px' }}>
            {t(
              'footprintDetailsPage.sections.calculatePcfSection.injectionMoldingSection.hopperDryerInfoText',
            )}
          </span>
        )}
      </div>
    </div>
  );
};

export default AuxiliaryMachineForm;
